/**
 * Training Detail Page - View training details, materials, and reviews
 */

import { useState, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import { learningApi } from '../api/learningApi'
import type { TrainingType, TrainingReview } from '../types/learning.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  course: 'Course',
  workshop: 'Workshop',
  certification: 'Certification',
  webinar: 'Webinar',
  seminar: 'Seminar',
  conference: 'Conference',
  other: 'Other',
}

const TRAINING_TYPE_COLORS: Record<TrainingType, string> = {
  course: 'bg-red-100 text-red-700',
  workshop: 'bg-rose-100 text-rose-700',
  certification: 'bg-green-100 text-green-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-700',
}

const DELIVERY_MODE_LABELS: Record<string, string> = {
  in_person: 'In Person',
  online: 'Online',
  hybrid: 'Hybrid',
}

const DELIVERY_MODE_ICONS: Record<string, JSX.Element> = {
  in_person: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  online: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  hybrid: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
}

type TabType = 'overview' | 'materials' | 'reviews'

const formatCurrency = (amount: number, currency?: string) => {
  const currencyCode = currency || 'IDR'
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatDuration = (hours: number) => {
  if (hours < 1) {
    return `${Math.round(hours * 60)} minutes`
  } else if (hours === 1) {
    return '1 hour'
  } else if (hours < 24) {
    return `${hours} hours`
  } else {
    const days = Math.floor(hours / 8)
    return days === 1 ? '1 day' : `${days} days`
  }
}

const TrainingDetailPage = () => {
  const { trainingId } = useParams<{ trainingId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('overview')

  // Review editing state
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null)
  const [editRating, setEditRating] = useState<number>(0)
  const [editFeedback, setEditFeedback] = useState<string>('')

  // Delete/Deactivate confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false)

  // Check if user is admin or HR
  const isAdminOrHR = user?.roles?.some(role =>
    role.name === 'admin' || role.name === 'hr'
  ) ?? false

  // Check if user can edit (admin or HR or manager)
  const canEdit = user?.roles?.some(role =>
    role.name === 'admin' || role.name === 'hr' || role.name === 'manager'
  ) ?? false

  // Fetch training details
  const { data: training, isLoading } = useQuery({
    queryKey: ['training', trainingId],
    queryFn: () => learningApi.getTraining(trainingId!),
    enabled: !!trainingId,
  })

  // Fetch training stats
  const { data: stats } = useQuery({
    queryKey: ['trainingStats', trainingId],
    queryFn: () => learningApi.getTrainingStats(trainingId!),
    enabled: !!trainingId,
  })

  // Fetch documents
  const { data: documentsData } = useQuery({
    queryKey: ['trainingDocuments', trainingId],
    queryFn: () => learningApi.listDocuments(trainingId!),
    enabled: !!trainingId,
  })

  // Fetch reviews from training_reviews table
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['trainingReviews', trainingId],
    queryFn: () => learningApi.getTrainingReviews(trainingId!, 1, 50),
    enabled: !!trainingId,
  })

  const documents = documentsData?.data || []
  // All reviews from the training_reviews table
  const allReviews = reviewsData?.data || []

  // Compute live average directly from fetched reviews (always accurate)
  const liveAvgRating = allReviews.length > 0
    ? Math.round((allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length) * 10) / 10
    : null
  const displayRating = liveAvgRating ?? training?.average_rating
  const displayCount = allReviews.length || training?.participant_count || 0

  // Check if user can upload documents
  const canUpload = canEdit || (training && training.creator?.id === user?.id)

  // Upload document mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => learningApi.uploadDocument(trainingId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingDocuments', trainingId] })
      toast.success('Document uploaded successfully')
      setIsUploading(false)
    },
    onError: () => {
      toast.error('Failed to upload document')
      setIsUploading(false)
    },
  })

  // Delete document mutation
  const deleteDocMutation = useMutation({
    mutationFn: (documentId: string) => learningApi.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingDocuments', trainingId] })
      toast.success('Document deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete document')
    },
  })

  // Update review mutation
  const updateReviewMutation = useMutation({
    mutationFn: (data: { rating: number; feedback: string }) =>
      learningApi.updateTrainingReview(trainingId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingReviews', trainingId] })
      queryClient.invalidateQueries({ queryKey: ['trainingStats', trainingId] })
      queryClient.invalidateQueries({ queryKey: ['training', trainingId] })
      toast.success('Review updated successfully')
      setEditingReviewId(null)
      setEditRating(0)
      setEditFeedback('')
    },
    onError: () => {
      toast.error('Failed to update review')
    },
  })

  // Delete training mutation
  const deleteMutation = useMutation({
    mutationFn: () => learningApi.deleteTraining(trainingId!),
    onSuccess: () => {
      toast.success('Training deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      navigate('/learning/trainings')
    },
    onError: () => {
      toast.error('Failed to delete training')
    },
  })

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      learningApi.updateTraining(trainingId!, { is_active: isActive }),
    onSuccess: (_, isActive) => {
      toast.success(isActive ? 'Training activated' : 'Training deactivated')
      queryClient.invalidateQueries({ queryKey: ['training', trainingId] })
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      setShowDeactivateConfirm(false)
    },
    onError: () => {
      toast.error('Failed to update training status')
    },
  })

  const handleEditReview = (review: TrainingReview) => {
    setEditingReviewId(review.id)
    setEditRating(review.rating)
    setEditFeedback(review.feedback || '')
  }

  const handleCancelEdit = () => {
    setEditingReviewId(null)
    setEditRating(0)
    setEditFeedback('')
  }

  const handleSaveReview = () => {
    if (editRating < 1 || editRating > 5) {
      toast.error('Please select a rating')
      return
    }
    updateReviewMutation.mutate({ rating: editRating, feedback: editFeedback })
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setIsUploading(true)
      uploadMutation.mutate(file)
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const renderStars = (rating: number | undefined, size: string = 'w-5 h-5', interactive: boolean = false) => {
    if (!rating && !interactive) return null
    const displayRating = rating || 0
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${size} ${star <= displayRating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  if (!training) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Training not found</h3>
          <p className="text-gray-500 mb-6">The training you're looking for doesn't exist.</p>
          <Link
            to="/learning/trainings"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Training Repository
          </Link>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { id: 'materials' as TabType, label: 'Materials', count: documents.length, icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { id: 'reviews' as TabType, label: 'Reviews', count: stats?.participant_count || 0, icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    )},
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/learning/trainings" className="hover:text-white transition-colors">Trainings</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white truncate max-w-xs">{training.name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${TRAINING_TYPE_COLORS[training.training_type]}`}>
                  {TRAINING_TYPE_LABELS[training.training_type]}
                </span>
                {training.category && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-white/20 text-white">
                    {training.category.name}
                  </span>
                )}
                {!training.is_active && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-500 text-yellow-900">
                    Inactive
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">{training.name}</h1>
              {training.provider && (
                <p className="text-red-200 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {training.provider}
                </p>
              )}

              {/* Rating Summary in Header — computed live from fetched reviews */}
              {displayRating && (
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-1">
                    {renderStars(Math.round(displayRating), 'w-5 h-5')}
                  </div>
                  <span className="text-white font-semibold">{displayRating.toFixed(1)}</span>
                  <span className="text-red-200">({displayCount} {displayCount === 1 ? 'review' : 'reviews'})</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {canEdit && (
                <button
                  onClick={() => navigate(`/learning/trainings/${training.id}/edit`)}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-white/20 text-white font-medium rounded-xl hover:bg-white/30 transition-all border border-white/30"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}
              {isAdminOrHR && (
                <>
                  <button
                    onClick={() => setShowDeactivateConfirm(true)}
                    className={`inline-flex items-center gap-2 px-5 py-3 font-medium rounded-xl transition-all border ${
                      training.is_active
                        ? 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30 hover:bg-yellow-500/30'
                        : 'bg-green-500/20 text-green-100 border-green-400/30 hover:bg-green-500/30'
                    }`}
                  >
                    {training.is_active ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Deactivate
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Activate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-red-500/20 text-red-100 font-medium rounded-xl hover:bg-red-500/30 transition-all border border-red-400/30"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </>
              )}
              <button
                onClick={() => navigate(`/learning/records/new?training=${training.id}`)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-red-700 font-medium rounded-xl hover:bg-red-50 transition-all shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add to My Records
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    activeTab === tab.id ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Description */}
                {training.description && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">About this Training</h2>
                    <p className="text-gray-600 whitespace-pre-wrap">{training.description}</p>
                  </div>
                )}

                {/* Course Details */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Course Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Delivery Mode */}
                    {training.delivery_mode && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                          {DELIVERY_MODE_ICONS[training.delivery_mode] || DELIVERY_MODE_ICONS.in_person}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Delivery Mode</p>
                          <p className="font-medium text-gray-900">{DELIVERY_MODE_LABELS[training.delivery_mode] || training.delivery_mode}</p>
                        </div>
                      </div>
                    )}

                    {/* Duration */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                        <p className="font-medium text-gray-900">
                          {training.duration_hours ? formatDuration(training.duration_hours) : <span className="text-gray-400 font-normal">Not set</span>}
                        </p>
                      </div>
                    </div>

                    {/* Fee */}
                    {training.fee !== undefined && training.fee !== null && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Fee</p>
                          <p className="font-medium text-gray-900">
                            {training.fee === 0 ? 'Free' : formatCurrency(training.fee, training.fee_currency)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Max Participants */}
                    {training.max_participants && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Max Participants</p>
                          <p className="font-medium text-gray-900">{training.max_participants} people</p>
                        </div>
                      </div>
                    )}

                    {/* Location */}
                    {training.location && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl md:col-span-2">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                          <p className="font-medium text-gray-900">{training.location}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vendor Information */}
                {(training.vendor_name || training.vendor_website || training.contact_person || training.contact_email || training.contact_phone) && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Vendor & Contact Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {training.vendor_name && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Vendor</p>
                            <p className="font-medium text-gray-900">{training.vendor_name}</p>
                          </div>
                        </div>
                      )}

                      {training.vendor_website && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Website</p>
                            <a
                              href={training.vendor_website.startsWith('http') ? training.vendor_website : `https://${training.vendor_website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-red-600 hover:text-red-700 hover:underline"
                            >
                              {training.vendor_website}
                            </a>
                          </div>
                        </div>
                      )}

                      {training.contact_person && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Contact Person</p>
                            <p className="font-medium text-gray-900">{training.contact_person}</p>
                          </div>
                        </div>
                      )}

                      {training.contact_email && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Email</p>
                            <a
                              href={`mailto:${training.contact_email}`}
                              className="font-medium text-red-600 hover:text-red-700 hover:underline"
                            >
                              {training.contact_email}
                            </a>
                          </div>
                        </div>
                      )}

                      {training.contact_phone && (
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Phone</p>
                            <a
                              href={`tel:${training.contact_phone}`}
                              className="font-medium text-red-600 hover:text-red-700 hover:underline"
                            >
                              {training.contact_phone}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Learning Information */}
                {(training.target_audience || training.prerequisites || training.learning_objectives || training.syllabus) && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Learning Information</h2>
                    <div className="space-y-6">
                      {training.target_audience && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Target Audience
                          </h3>
                          <p className="text-gray-600 whitespace-pre-wrap">{training.target_audience}</p>
                        </div>
                      )}

                      {training.prerequisites && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            Prerequisites
                          </h3>
                          <p className="text-gray-600 whitespace-pre-wrap">{training.prerequisites}</p>
                        </div>
                      )}

                      {training.learning_objectives && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                            Learning Objectives
                          </h3>
                          <div
                            className="prose prose-sm prose-gray max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-700 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5"
                            dangerouslySetInnerHTML={{ __html: training.learning_objectives }}
                          />
                        </div>
                      )}

                      {training.syllabus && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            Syllabus
                          </h3>
                          <div
                            className="prose prose-sm prose-gray max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-700 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5"
                            dangerouslySetInnerHTML={{ __html: training.syllabus }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Materials Tab */}
            {activeTab === 'materials' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Training Materials</h2>
                    <p className="text-sm text-gray-500 mt-1">Documents, slides, and resources for this training</p>
                  </div>
                  {canUpload && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {isUploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.jpg,.jpeg,.png"
                />

                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-red-200 hover:bg-red-50/50 transition-colors group"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">{doc.original_filename}</p>
                            <p className="text-sm text-gray-500">
                              {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                              {doc.uploaded_at && ` • Uploaded ${format(new Date(doc.uploaded_at), 'MMM d, yyyy')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={async () => {
                              try {
                                const blob = await learningApi.downloadDocument(doc.id)
                                const url = window.URL.createObjectURL(blob)
                                window.open(url, '_blank')
                              } catch {
                                toast.error('Failed to open document')
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="View"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const blob = await learningApi.downloadDocument(doc.id)
                                const url = window.URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = doc.original_filename
                                document.body.appendChild(a)
                                a.click()
                                document.body.removeChild(a)
                                window.URL.revokeObjectURL(url)
                              } catch {
                                toast.error('Failed to download document')
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Download"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                          {canUpload && (
                            <button
                              onClick={() => {
                                if (confirm('Are you sure you want to delete this document?')) {
                                  deleteDocMutation.mutate(doc.id)
                                }
                              }}
                              disabled={deleteDocMutation.isPending}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No materials uploaded yet</h3>
                    <p className="text-gray-500 mb-4">Training documents and resources will appear here</p>
                    {canUpload && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Upload First Document
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Rating Summary */}
                {stats && stats.participant_count > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-8">
                      <div className="text-center md:text-left">
                        <div className="text-5xl font-bold text-gray-900 mb-2">
                          {stats.average_rating?.toFixed(1) || '0.0'}
                        </div>
                        {renderStars(stats.average_rating ? Math.round(stats.average_rating) : 0, 'w-6 h-6')}
                        <p className="text-sm text-gray-500 mt-2">Based on {stats.participant_count} reviews</p>
                      </div>
                      <div className="flex-1 space-y-2">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count = stats.rating_distribution?.[star] || 0
                          const total = Object.values(stats.rating_distribution || {}).reduce((a, b) => a + b, 0)
                          const percent = total > 0 ? (count / total) * 100 : 0
                          return (
                            <div key={star} className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-600 w-3">{star}</span>
                              <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-yellow-400 rounded-full transition-all"
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-500 w-8 text-right">{count}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Reviews List */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Participant Reviews</h2>
                  </div>

                  {reviewsLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-600 mx-auto"></div>
                      <p className="mt-4 text-gray-500">Loading reviews...</p>
                    </div>
                  ) : allReviews.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {allReviews.map((review: TrainingReview) => {
                        const isOwnReview = review.employee?.id === user?.id
                        const isEditing = editingReviewId === review.id

                        return (
                          <div key={review.id} className="p-6 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                {review.employee?.full_name?.charAt(0) || review.employee?.email?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                {isEditing ? (
                                  /* Edit Mode */
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                      <h4 className="font-medium text-gray-900">
                                        {review.employee?.full_name || 'Anonymous'}
                                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Editing</span>
                                      </h4>
                                    </div>
                                    {/* Star Rating Selector */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                                      <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => setEditRating(star)}
                                            className="focus:outline-none"
                                          >
                                            <svg
                                              className={`w-8 h-8 transition-colors ${star <= editRating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'}`}
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                          </button>
                                        ))}
                                        <span className="ml-2 text-sm text-gray-500">{editRating}/5</span>
                                      </div>
                                    </div>
                                    {/* Feedback Textarea */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Feedback</label>
                                      <textarea
                                        value={editFeedback}
                                        onChange={(e) => setEditFeedback(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                        placeholder="Share your thoughts about this training..."
                                      />
                                    </div>
                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={handleSaveReview}
                                        disabled={updateReviewMutation.isPending}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        {updateReviewMutation.isPending ? 'Saving...' : 'Save Changes'}
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        disabled={updateReviewMutation.isPending}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* View Mode */
                                  <>
                                    <div className="flex items-start justify-between gap-4">
                                      <div>
                                        <h4 className="font-medium text-gray-900">
                                          {review.employee?.full_name || 'Anonymous'}
                                          {isOwnReview && (
                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">You</span>
                                          )}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                          {renderStars(review.rating, 'w-4 h-4')}
                                          <span className="text-sm font-medium text-gray-700">{review.rating}/5</span>
                                          {review.created_at && (
                                            <span className="text-sm text-gray-500">
                                              • {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {/* Edit Button for own review */}
                                      {isOwnReview && (
                                        <button
                                          onClick={() => handleEditReview(review)}
                                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          Edit
                                        </button>
                                      )}
                                    </div>

                                    {/* Feedback Section */}
                                    {review.feedback ? (
                                      <div className="mt-4 p-4 bg-gray-50 rounded-xl border-l-4 border-red-400">
                                        <p className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                          </svg>
                                          Feedback
                                        </p>
                                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{review.feedback}</p>
                                      </div>
                                    ) : (
                                      <p className="mt-3 text-sm text-gray-400 italic">No written feedback provided</p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                      <p className="text-gray-500">Be the first to leave a review for this training!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Statistics</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Participants</span>
                  <span className="font-semibold text-gray-900">{displayCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Average Rating</span>
                  <div className="flex items-center gap-1">
                    {displayRating ? (
                      <>
                        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="font-semibold text-gray-900">{displayRating.toFixed(1)}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">No ratings</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Added</span>
                  <span className="font-semibold text-gray-900">
                    {format(new Date(training.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </div>

            {/* Added By */}
            {training.creator && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Added By</h3>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center text-white font-semibold">
                    {training.creator.full_name?.charAt(0) || training.creator.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{training.creator.full_name || 'Unknown'}</p>
                    <p className="text-sm text-gray-500">{training.creator.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3">Quick Actions</h3>
              <button
                onClick={() => navigate(`/learning/records/new?training=${training.id}`)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add to My Records
              </button>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/learning/trainings')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Training Repository
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Training</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{training.name}</strong>"? This will remove the training from the repository.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate/Activate Confirmation Modal */}
      {showDeactivateConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${training.is_active ? 'bg-yellow-100' : 'bg-green-100'}`}>
                {training.is_active ? (
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {training.is_active ? 'Deactivate' : 'Activate'} Training
                </h3>
                <p className="text-sm text-gray-500">
                  {training.is_active
                    ? 'Training will no longer be available for selection'
                    : 'Training will be available for selection again'}
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              {training.is_active
                ? `Are you sure you want to deactivate "${training.name}"? Employees will not be able to select this training for new records.`
                : `Are you sure you want to activate "${training.name}"? Employees will be able to select this training again.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeactivateConfirm(false)}
                disabled={toggleActiveMutation.isPending}
                className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => toggleActiveMutation.mutate(!training.is_active)}
                disabled={toggleActiveMutation.isPending}
                className={`px-4 py-2 font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  training.is_active
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {toggleActiveMutation.isPending
                  ? (training.is_active ? 'Deactivating...' : 'Activating...')
                  : (training.is_active ? 'Deactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TrainingDetailPage
